import { useRef, useState, useEffect } from 'react';
import { CanvasElement as CanvasElementType, IconElement } from '@/types/canvas';
import { AVAILABLE_ICONS } from '@/config/icons';

interface CanvasElementProps {
  element: CanvasElementType;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (updates: Partial<CanvasElementType>) => void;
}

export function CanvasElement({ element, isSelected, onSelect, onUpdate }: CanvasElementProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).classList.contains('resize-handle')) {
      return;
    }
    e.stopPropagation();
    e.preventDefault();
    
    // Vybrat element pouze pokud není už vybraný
    if (!isSelected) {
      onSelect();
    }
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - element.x,
      y: e.clientY - element.y,
    });
  };

  const handleResizeStart = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: element.width,
      height: element.height,
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        onUpdate({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      } else if (isResizing) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        onUpdate({
          width: Math.max(20, resizeStart.width + deltaX),
          height: Math.max(20, resizeStart.height + deltaY),
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, dragStart, resizeStart, onUpdate]);

  const renderContent = () => {
    if (element.type === 'icon') {
      const iconEl = element as IconElement;
      const IconComponent = AVAILABLE_ICONS[iconEl.iconName] || AVAILABLE_ICONS.MapPin;
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <IconComponent
            style={{ color: iconEl.color }}
            className="w-full h-full"
          />
          {iconEl.label && (
            <span
              className="text-xs mt-1 whitespace-nowrap"
              style={{ color: iconEl.color }}
            >
              {iconEl.label}
            </span>
          )}
        </div>
      );
    }
    
    return null;
  };

  return (
    <div
      ref={elementRef}
      style={{
        position: 'absolute',
        left: `${element.x}px`,
        top: `${element.y}px`,
        width: `${element.width}px`,
        height: `${element.height}px`,
        transform: `rotate(${element.rotation}deg)`,
        transformOrigin: 'center',
        cursor: isDragging ? 'grabbing' : 'grab',
        zIndex: element.zIndex,
      }}
      onMouseDown={handleMouseDown}
      className={`${isSelected ? 'ring-2 ring-blue-500' : ''}`}
    >
      {renderContent()}
      
      {isSelected && (
        <>
          {/* Resize handles */}
          <div
            className="resize-handle absolute bottom-0 right-0 w-3 h-3 bg-blue-500 cursor-nwse-resize"
            onMouseDown={handleResizeStart}
            style={{ transform: 'translate(50%, 50%)' }}
          />
          <div
            className="resize-handle absolute top-0 right-0 w-3 h-3 bg-blue-500 cursor-nesw-resize"
            onMouseDown={handleResizeStart}
            style={{ transform: 'translate(50%, -50%)' }}
          />
          <div
            className="resize-handle absolute bottom-0 left-0 w-3 h-3 bg-blue-500 cursor-nesw-resize"
            onMouseDown={handleResizeStart}
            style={{ transform: 'translate(-50%, 50%)' }}
          />
          <div
            className="resize-handle absolute top-0 left-0 w-3 h-3 bg-blue-500 cursor-nwse-resize"
            onMouseDown={handleResizeStart}
            style={{ transform: 'translate(-50%, -50%)' }}
          />
        </>
      )}
    </div>
  );
}

